// The account/entitlement session model — client-only (localStorage, no
// server state), read by the registry's `entitlement()` closures (§4 of
// oci-app-platform-foundation.md: "reuses hasEntitlement()") and by every
// dashboard/ page that gates on role or entitlement. Lives here, under
// src/miniapp/, rather than in dashboard/src/, so the registry (which the
// platform spec commits to first) and the pre-existing dashboard SPA read
// the exact same module instead of two copies drifting apart the way the
// design-token values did before designTokens.ts. dashboard/src/session.ts
// is now a thin re-export of this file — see the comment there.
import { isOperatorRole } from '../platform/houseAccounts.js';

export interface User {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'operator' | 'owner' | 'staff' | 'collaborator';
  accountId: number | null;
  accountSlug: string | null;
  accountName: string | null;
  // Raw wire fields from the backend session user — see the entitlement
  // predicates below, which are the only sanctioned way to read these.
  tier: string | null;
  tiers: string[];
  /** Whether this account owns at least one gig run — registry.ts's own signal for the Gigs mini-app; see authService.ts's AuthUser for why it is not folded into `tiers`. */
  hasGigs: boolean;
  /** The apps this person's membership opens in the account they are in;
   *  null is every app the account holds (src/platform/apps.ts). Always
   *  null for an operator. Absent from an older server's answer. */
  apps?: string[] | null;
  /** Every account this person can work in, for the shell's switcher. */
  memberships?: MembershipSummary[];
}

/** One account a person belongs to, as /api/me lists it. */
export interface MembershipSummary {
  accountSlug: string;
  accountName: string | null;
  role: 'owner' | 'staff' | 'collaborator';
  apps: string[] | null;
}

/** Whether the signed-in person's membership lets them into this app. An
 *  operator's reach is not a membership's, so it is never narrowed here. */
export function membershipOpens(appId: string): boolean {
  if (isOperator()) return true;
  const apps = _user?.apps ?? null;
  return apps === null || apps.includes(appId);
}

/** Whether the signed-in person works only inside named apps — no account
 *  settings, no billing. */
export function isCollaborator(): boolean {
  return _user?.role === 'collaborator';
}

/** Work in another account this person belongs to; the page reloads into it. */
export async function switchAccount(slug: string): Promise<void> {
  const res = await fetch('/api/me/account', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slug }),
  });
  if (!res.ok) throw new Error('could_not_switch');
  window.location.href = '/app';
}

/** A house account (src/platform/houseAccounts.ts) as handed to an
 *  operator/admin on /api/login and /api/me — OCI's own account, carried to
 *  the client so an operator with nothing explicitly view-as'd still
 *  resolves entitlements against something instead of an empty set. */
export interface HouseAccount {
  slug: string;
  name: string;
  tiers: string[];
}

// ── Entitlement ──────────────────────────────────────────────────────────────
// There are no tiers (operator, 2026-09-05): an entitlement is what an account
// has bought or has running, and product boundaries gate on that — never on a
// tier string. `_user.tiers` is already exactly that set (the backend reads
// it from the account's active platform.account_tiers rows), so it is derived
// here rather than carried in under "tier" vocabulary anywhere else.

// While an operator is viewing-as an account (below), entitlement reads must
// answer for the VIEWED account, not the operator's own session — an
// operator's own `_user.tiers` is always empty (operators hold no account of
// their own), so without this every entitlement-gated surface would stay
// hidden throughout view-as instead of rendering the way that account's owner
// actually sees it.
export function getEntitlements(): string[] {
  if (isOperator() && selectedAccountSlug()) return getViewAsTiers();
  if (isOperator()) return defaultAccount()?.tiers ?? [];
  return _user?.tiers ?? [];
}

export function hasEntitlement(key: string): boolean {
  return getEntitlements().includes(key);
}

export function hasMultipleEntitlements(): boolean {
  return getEntitlements().length > 1;
}

/**
 * Gigs' own entitlement rule — deliberately NOT hasEntitlement('gigs'). A gig
 * buyer account (signupService.findOrCreateGigBuyerAccount) carries `tier:
 * null` and no platform.account_tiers rows, so getEntitlements() can never
 * return anything for it; Gigs was never bought as a tier, it was bought as
 * one-off runs. `hasGigs` is authService.ts's own signal for exactly that
 * (an EXISTS check against platform.gig_runs), carried on the session user
 * rather than derived here.
 *
 * Known gap: operator view-as (setViewAs/getViewAsTiers above) only carries
 * `tiers` into localStorage, not `hasGigs` — an operator viewing-as an
 * account that owns gig runs but no product tier will not see the Gigs entry
 * in that account's sidebar the way the account's own owner would. Left as a
 * limitation rather than guessed at further; view-as parity for a
 * non-tier entitlement is a product question, not a seat's call.
 */
export function hasGigsEntitlement(): boolean {
  return _user?.hasGigs === true;
}

export function getAccountName(): string | null {
  return _user?.accountName ?? null;
}

let _user: User | null = null;

export function getUser(): User | null { return _user; }
export function setUser(u: User | null): void { _user = u; }

let _houseAccounts: HouseAccount[] = [];
export function setHouseAccounts(accounts: HouseAccount[]): void { _houseAccounts = accounts; }

/** The one account an operator with nothing explicitly view-as'd still has
 *  context for — OCI's own house account (src/platform/houseAccounts.ts),
 *  so an operator signing in resolves entitlements against OCI's own tiers
 *  instead of an empty set. An explicit view-as (setViewAs) always wins:
 *  this is only ever consulted when selectedAccountSlug() is null. */
export function defaultAccount(): HouseAccount | null {
  return _houseAccounts[0] ?? null;
}

/**
 * The one /api/me fetch a page makes, memoized for its lifetime. Client-only
 * — call it from an effect, never at render (see Shell.ts).
 *
 * Memoized because more than one component in the shell build needs the
 * signed-in user before it can draw honestly: Shell.ts for the sidebar, and
 * StudioMiniApp.ts for a tab set that depends on which grants the account
 * holds. Without this they each fetched, and the second one also had to
 * assume something about the order two sibling effects run in. Resolves to
 * null for a signed-out visitor rather than rejecting, because "nobody is
 * signed in" is an answer and not a failure.
 */
let _sessionLoad: Promise<User | null> | null = null;
let _sessionSettled = false;
export function loadSession(): Promise<User | null> {
  if (!_sessionLoad) {
    _sessionLoad = fetch('/api/me', { credentials: 'include' })
      .then(async res => {
        if (!res.ok) throw new Error('unauthorized');
        return (await res.json()) as { user: User; houseAccounts?: HouseAccount[] };
      })
      .then(({ user, houseAccounts }) => { setUser(user); setHouseAccounts(houseAccounts ?? []); return user; })
      .catch(() => { setUser(null); setHouseAccounts([]); return null; })
      .then(user => { _sessionSettled = true; return user; });
  }
  return _sessionLoad;
}

/** Whether loadSession() has answered, signed in or not — read synchronously,
 *  so a component mounted after that (a client-side navigation) can render
 *  its content on the first pass instead of waiting a tick for a promise that
 *  has already resolved. Always false during SSR, where nothing calls
 *  loadSession(). */
export function sessionSettled(): boolean {
  return _sessionSettled;
}

export function isOperator(): boolean {
  return isOperatorRole(_user?.role);
}

/** True only when an operator is acting under the implicit house-account
 *  default (defaultAccount()) rather than an explicit view-as — i.e. there
 *  is live account context, but nobody chose it. Drives the shell's banner
 *  so a mutation is never made against real data while it looks inert. */
export function isImplicitHouseContext(): boolean {
  return isOperator() && !selectedAccountSlug() && !!defaultAccount();
}

// ── Account resolution (client side) ────────────────────────────────────────
// Spec: docs/specs/account-resolution-overhaul.md §4. The server is the single
// source of truth (requireAccountAccess); these helpers only decide what the
// client should *send*, split by intent so a page declares what it means.

/** The caller's OWN account (owner/staff), from the session-loaded user. Null
 *  for operators. Use for display and for resource-PATH URLs. */
export function ownAccountSlug(): string | null {
  return _user?.accountSlug ?? null;
}

/** The operator's currently-selected account — entered via the view-as action
 *  on a client's row (AccountsPage), not a global switcher. Null for owners,
 *  or when an operator is not viewing any account.
 *
 *  Stored in localStorage, not sessionStorage: view-as is meant to survive a
 *  closed tab and a fresh login, not just a page navigation — an operator who
 *  steps into an account (their own dogfood account included) stays there
 *  until they explicitly exit (ViewAsBanner) or switch, rather than being
 *  reset to the raw operator feed every time they sign back in. */
export function selectedAccountSlug(): string | null {
  return localStorage.getItem('oci_slug') || null;
}

/** The account currently in context: the owner's own, or the operator's
 *  selection. Use for resource-PATH URLs (`/api/agent/:slug/…`) where the slug
 *  is required for every role, and for "is an account in context?" checks. */
export function currentAccountSlug(): string | null {
  return ownAccountSlug() ?? selectedAccountSlug() ?? (isOperator() ? defaultAccount()?.slug ?? null : null);
}

/** The slug to send on QUERY-form calls (`/api/x?account_slug=…`). Null for
 *  owner/staff — the server resolves their account from the session, so the
 *  client must not over-send. Operators send their selection. */
export function requestAccountSlug(): string | null {
  return isOperator() ? (selectedAccountSlug() ?? defaultAccount()?.slug ?? null) : null;
}

export function setAccountSlug(slug: string): void {
  localStorage.setItem('oci_slug', slug);
}

const VIEW_AS_NAME_KEY = 'oci_slug_name';
const VIEW_AS_TIERS_KEY = 'oci_slug_tiers';

/** Enter view-as: the account slug (existing mechanism above) plus its display
 *  name (so the shell's banner — shellModel.ts — has one without a fetch) and
 *  its entitlements (so navModel()/homeSurface() can render that account's
 *  actual owner surface instead of the operator's own, always-empty, tiers). */
export function setViewAs(slug: string, name: string, tiers: string[]): void {
  setAccountSlug(slug);
  localStorage.setItem(VIEW_AS_NAME_KEY, name);
  localStorage.setItem(VIEW_AS_TIERS_KEY, JSON.stringify(tiers));
}

/** Exit view-as: back to the operator's own, unscoped view. */
export function clearViewAs(): void {
  localStorage.removeItem('oci_slug');
  localStorage.removeItem(VIEW_AS_NAME_KEY);
  localStorage.removeItem(VIEW_AS_TIERS_KEY);
}

export function getViewAsAccountName(): string | null {
  return localStorage.getItem(VIEW_AS_NAME_KEY) || null;
}

function getViewAsTiers(): string[] {
  try {
    const raw = localStorage.getItem(VIEW_AS_TIERS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Role-correct message when an account-scoped page has no account in context.
 *  Operators must pick one; for owners a blank is only ever a transient load —
 *  never the old "enter a slug in the nav" operator-vocabulary leak. */
export function noAccountMessage(): string {
  return isOperator()
    ? 'Pick a client from Accounts, then click View to see their dashboard.'
    : 'Your account is still loading — refresh if this persists.';
}

// requireAccountAccess.ts's AccessDecision.error codes, translated for
// display. Same wire contract dashboard/src/api.ts's own copy of this table
// translates (that file is unreachable from here — tsconfig.web.json's
// rootDir excludes dashboard/ from every src/miniapp/ build — so this is a
// second copy, not a shared import, same discipline as every other
// re-implemented-not-imported helper in this directory).
// Never show one of these codes verbatim (2026-09-24: "no_active_grant" was
// rendering as page text on Call List/Agent Activity) — every native
// mini-app's local apiFetch must route its thrown error message through
// this before it reaches component state.
const ACCOUNT_ACCESS_ERROR_COPY: Record<string, string> = {
  unauthorized: 'Your session has expired. Sign in again.',
  forbidden_slug: "You don't have access to that account.",
  account_slug_required: 'No account is selected.',
  no_active_grant: "You don't have access to this account's data right now.",
  account_not_found: 'That account could not be found.',
  invalid_slug: 'That account link looks wrong.',
  forbidden: "You don't have permission to view this.",
};

/** The copy a no_active_grant error arrives as once translated — what
 *  shared/AccessRequest.ts recognises to offer an operator the grant request
 *  in its place, rather than a line that ends the page. */
export const NO_ACTIVE_GRANT_MESSAGE = ACCOUNT_ACCESS_ERROR_COPY['no_active_grant']!;

/** Maps a backend error code to copy a person can read; passes anything
 *  unrecognized through unchanged rather than hiding it. */
export function friendlyApiError(code: string): string {
  return ACCOUNT_ACCESS_ERROR_COPY[code] ?? code;
}
